import { getFirestoreClient } from "../lib/firestoreClient";
import { createLogger } from "../utils/logger";
import { Ok, Err } from "../types/result";
import type { Result } from "../types/result";
import type { AppError } from "../types/errors";
import {
  mapFirebaseError,
  createValidationError,
  createUnexpectedError,
} from "../types/errors";
import type { Scenario, ScenarioSaveInput } from "../types/scenario";
import { updateSettings } from "./settingsService";
import type { SettingsCategory } from "../types/settings";

const logger = createLogger({ prefix: "ScenarioService" });

/** Convert a Firestore Timestamp, plain Date, or unknown value to a Date. */
function toDate(value: unknown): Date {
  if (
    value !== null &&
    typeof value === "object" &&
    typeof (value as Record<string, unknown>).toDate === "function"
  ) {
    return (value as { toDate(): Date }).toDate();
  }
  if (value instanceof Date) return value;
  return new Date();
}

/**
 * ScenarioService manages scenario persistence in Firestore
 * Handles CRUD operations for user scenarios with validation and error handling
 */
export class ScenarioService {
  private maxScenarios: number;

  constructor(options?: { maxScenarios?: number }) {
    this.maxScenarios = options?.maxScenarios || 5;
  }

  /**
   * List all scenarios for a user, ordered by most recent
   */
  async listScenarios(userId: string): Promise<Result<Scenario[], AppError>> {
    try {
      if (!userId) {
        return Err(
          createUnexpectedError({
            error: new Error("Missing userId"),
            context: "listScenarios called without userId",
          })
        );
      }

      const client = getFirestoreClient();
      const collPath = ["users", userId, "scenarios"];
      const snapshot = await client.queryCollection(collPath, [
        { type: "orderBy", field: "updatedAt", direction: "desc" },
      ]);

      const scenarios = snapshot.docs.map((docSnap) => {
        const data = docSnap.data()!;
        return {
          id: docSnap.id,
          userId,
          name: data.name,
          description: data.description || "",
          settings: data.settings,
          projections: data.projections as Scenario["projections"],
          createdAt: toDate(data.createdAt),
          updatedAt: toDate(data.updatedAt),
        } as Scenario;
      });

      return Ok(scenarios);
      /* v8 ignore start - Defensive catch for exceptional errors outside normal flow */
    } catch (error) {
      logger.error("Failed to list scenarios", { error, userId });
      return Err(mapFirebaseError(error));
    }
    /* v8 ignore stop */
  }

  /**
   * Save a new scenario or replace an existing one
   * Note: Projections are NOT stored - they are computed on-the-fly when scenarios are loaded
   */
  async saveScenario(
    input: ScenarioSaveInput
  ): Promise<Result<Scenario, AppError>> {
    try {
      const { userId, name, description, settings, maxScenarios } = input;
      const limit = maxScenarios ?? this.maxScenarios;

      if (!userId) {
        return Err(
          createUnexpectedError({
            error: new Error("Missing userId"),
            context: "saveScenario called without userId",
          })
        );
      }

      if (!name || name.trim().length === 0) {
        return Err(
          createValidationError({
            fields: ["name"],
            messages: { name: "Scenario name is required" },
          })
        );
      }

      if (name.length > 50) {
        return Err(
          createValidationError({
            fields: ["name"],
            messages: { name: "Scenario name must be 50 characters or less" },
          })
        );
      }

      if (description && description.length > 200) {
        return Err(
          createValidationError({
            fields: ["description"],
            messages: {
              description: "Description must be 200 characters or less",
            },
          })
        );
      }

      // Check scenario limit
      const listResult = await this.listScenarios(userId);
      if (!listResult.ok) {
        return listResult as Result<Scenario, AppError>;
      }

      if (listResult.value.length >= limit) {
        return Err(
          createValidationError({
            fields: ["scenarioLimit"],
            messages: {
              scenarioLimit: `Maximum ${limit} scenarios allowed`,
            },
          })
        );
      }

      // Create new scenario
      const client = getFirestoreClient();
      const collPath = ["users", userId, "scenarios"];
      const scenarioId = client.generateId(collPath);

      const scenario: Scenario = {
        id: scenarioId,
        userId,
        name: name.trim(),
        description: description?.trim() || "",
        settings,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const scenarioPath = ["users", userId, "scenarios", scenarioId];
      await client.setDocument(
        scenarioPath,
        {
          userId,
          name: scenario.name,
          description: scenario.description,
          settings,
          createdAt: client.serverTimestamp(),
          updatedAt: client.serverTimestamp(),
        } as Record<string, unknown>,
        { merge: true }
      );

      logger.info(`Scenario saved successfully`, {
        userId,
        scenarioId,
        name,
      });
      return Ok(scenario);
    } catch (error) {
      logger.error("Failed to save scenario", { error, name: input.name });
      return Err(mapFirebaseError(error));
    }
  }

  /**
   * Get a single scenario by ID
   */
  async getScenario(
    userId: string,
    scenarioId: string
  ): Promise<Result<Scenario | null, AppError>> {
    try {
      if (!userId) {
        return Err(
          createUnexpectedError({
            error: new Error("Missing userId"),
            context: "getScenario called without userId",
          })
        );
      }

      const client = getFirestoreClient();
      const scenarioPath = ["users", userId, "scenarios", scenarioId];
      const snapshot = await client.getDocument(scenarioPath);

      if (!snapshot.exists()) {
        return Ok(null);
      }

      const data = snapshot.data()!;
      const scenario: Scenario = {
        id: snapshot.id,
        userId,
        name: data.name as string,
        description: (data.description as string) || "",
        settings: data.settings as Scenario["settings"],
        projections: data.projections as Scenario["projections"],
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
      };
      return Ok(scenario);
      /* v8 ignore start - Defensive catch for exceptional errors outside normal flow */
    } catch (error) {
      logger.error("Failed to get scenario", { error, userId, scenarioId });
      return Err(mapFirebaseError(error));
    }
    /* v8 ignore stop */
  }

  /**
   * Delete a scenario by ID
   */
  async deleteScenario(
    userId: string,
    scenarioId: string
  ): Promise<Result<void, AppError>> {
    try {
      if (!userId) {
        return Err(
          createUnexpectedError({
            error: new Error("Missing userId"),
            context: "deleteScenario called without userId",
          })
        );
      }

      const client = getFirestoreClient();
      const scenarioPath = ["users", userId, "scenarios", scenarioId];
      await client.deleteDocument(scenarioPath);

      logger.info(`Scenario deleted successfully`, { userId, scenarioId });
      return Ok(undefined);
      /* v8 ignore start - Defensive catch for exceptional errors outside normal flow */
    } catch (error) {
      logger.error("Failed to delete scenario", { error, userId, scenarioId });
      return Err(mapFirebaseError(error));
    }
    /* v8 ignore stop */
  }

  /**
   * Apply a scenario's settings to the user's current settings
   */
  async applyScenarioSettings(
    userId: string,
    scenario: Scenario
  ): Promise<Result<void, AppError>> {
    try {
      if (!userId) {
        return Err(
          createUnexpectedError({
            error: new Error("Missing userId"),
            context: "applyScenarioSettings called without userId",
          })
        );
      }

      const categories: SettingsCategory[] = [
        "profile",
        "investableAssets",
        "realEstateAssets",
        "liabilities",
        "income",
        "expenses",
        "assumptions",
      ];

      // Apply each settings category
      for (const category of categories) {
        const settingsData = scenario.settings[category];
        if (settingsData && Object.keys(settingsData).length > 0) {
          const result = await updateSettings(userId, category, settingsData);
          /* v8 ignore start - updateSettings is sync (enqueue) and always returns ok */
          if (!result.ok) {
            logger.error(`Failed to apply ${category} settings`, {
              error: result.error,
            });
            return result as Result<void, AppError>;
          }
          /* v8 ignore stop */
        }
      }

      logger.info(`Scenario settings applied successfully`, {
        userId,
        scenarioId: scenario.id,
      });
      return Ok(undefined);
      /* v8 ignore start - Defensive catch for exceptional errors outside normal flow */
    } catch (error) {
      logger.error("Failed to apply scenario settings", { error });
      return Err(mapFirebaseError(error));
    }
    /* v8 ignore stop */
  }
}

// Export singleton instance
export const scenarioService = new ScenarioService();
